import React, { useCallback } from 'react';
import { toast } from 'react-toastify';
import { useCallbackObservable, useObservable } from 'react-use-observable';

import { Button, Modal } from 'antd';

import Spin from 'components/Spin';
import { Formik, Form, Field } from 'formik';
import type { FormikProps } from 'formik';
import { of } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import { ListRadioButtonFormField } from 'v2/components/Shared/FormFields/ListRadioButton';
import type { ERefundMotiveId, ISendRefund } from 'v2/interfaces/requestManagement/refund';
import ordersService from 'v2/services/orders';
import { refundRequestFactory } from 'v2/services/shopping/refundRequest';

import { validationSchema } from './validation';

interface IFormikValues {
  motive_id: ERefundMotiveId;
  motive: string;
}

export function ModalRefund() {
  const [open] = useObservable(() => ordersService.getModalRefund(), []);
  const [invoice] = useObservable(() => ordersService.getInvoice(), []);
  const onClose = useCallback(() => ordersService.closeModalRefund(), []);

  const [motives] = useObservable(() => refundRequestFactory.getRefundMotive(), []);
  const [loading] = useObservable(() => refundRequestFactory.loading(), []);

  const onHandleClose = useCallback(
    (resetForm: () => void) => {
      onClose();
      resetForm();
    },
    [onClose]
  );

  const [onSubmit] = useCallbackObservable(
    (values: IFormikValues, { setSubmitting, resetForm }: FormikProps<IFormikValues>) => {
      setSubmitting(true);

      const { description } = motives.find(motive => motive.id === values.motive_id) || {};

      const formattedValues: ISendRefund = {
        motive_id: values.motive_id,
        invoice_id: invoice.id,
        motive: description
      };

      return refundRequestFactory.sendRefundRequest(formattedValues).pipe(
        tap(() => {
          setSubmitting(false);
          onHandleClose(resetForm);
          toast.success('Solicitação realizada com sucesso!');
        }),
        catchError(err => {
          setSubmitting(false);
          console.error(err);
          toast.error('Não foi possível reembolsar a fatura.');
          return of(true);
        })
      );
    },
    [motives, invoice]
  );

  const motiveOptions = (motives || []).map(item => ({
    label: item.description,
    value: item.id
  }));

  return (
    <Formik validationSchema={validationSchema} initialValues={{ motive_id: '', motive: '' }} onSubmit={onSubmit}>
      {({ isSubmitting, submitForm, resetForm, isValid, dirty }) => {
        const shouldDisableConfirmBtn = !isValid || isSubmitting || !dirty;

        return (
          <Form>
            <Modal
              open={open}
              onCancel={() => onHandleClose(resetForm)}
              title={<span className='text-lg font-medium'>Deseja realmente reembolsar essa fatura?</span>}
              width={600}
              footer={
                <div className='flex justify-end gap-2 px-2 py-1'>
                  <Button onClick={() => onHandleClose(resetForm)}>Cancelar</Button>
                  <Button onClick={submitForm} type='primary' disabled={shouldDisableConfirmBtn}>
                    Confirmar
                  </Button>
                </div>
              }
              destroyOnClose
            >
              {loading || isSubmitting ? (
                <div className='p-4 text-center'>
                  <Spin />
                </div>
              ) : (
                <div className='py-2'>
                  <Field id='motive_id' name='motive_id' options={motiveOptions} component={ListRadioButtonFormField} />
                </div>
              )}
            </Modal>
          </Form>
        );
      }}
    </Formik>
  );
}
